<script setup lang="ts">
import {ref, useTemplateRef} from "vue";
import {ICellRendererParams} from "ag-grid-community";
import type {QUploader} from "quasar";
import EditScreenGridHandler from "@/utils/common/EditScreenGridHandler.ts";
import FileUploaderWidgetUtil from "@/utils/editView/widgets/FileUploaderWidgetUtil.ts";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import {useLoadingStore} from "@/stores/LoadingStore.ts";
import ImagePreviewer from "@/components/dialogs/ImagePreviewer.vue";
import FieldMetaData = namaTypes.FieldMetaData;

const props = defineProps<{ params: ICellRendererParams }>();
const handler: EditScreenGridHandler = props.params["handler"]()
const columnMetadata: FieldMetaData = props.params["columnMetadata"]
const imageVisible = ref(false)
const qUploaderRef = useTemplateRef<QUploader>("qUploaderRef")
const loadingStore = useLoadingStore();
const colId = props.params["columnId"];
let mouseIn = false;
const agFieldUtil = new FileUploaderWidgetUtil(handler.presenter, columnMetadata, colId, props.params)
const isColumnDisabled = (): boolean => {
  return handler.presenter.isColumnDisabled(props.params.data.id, handler.gridId, colId)
}

async function startUpload(e: MouseEvent) {
  if (qUploaderRef.value)
    qUploaderRef.value.pickFiles(e);
}

async function onAgAttachmentClick(e: MouseEvent, forceUpload: boolean) {
  if (isColumnDisabled())
    return;
  if (agFieldUtil.isAttachmentExist() && !forceUpload)
    return;
  if (e.altKey || e.ctrlKey)
    await FileUploaderWidgetUtil.startScan(agFieldUtil);
  else
    await startUpload(e);
}

async function onScanClick(e: MouseEvent) {
  if (isColumnDisabled())
    return;
  if (e.altKey || e.ctrlKey) {
    await startUpload(e);
  } else {
    await FileUploaderWidgetUtil.startScan(agFieldUtil);
  }
}

function onMouseEnter() {
  mouseIn = true;
  if (imageVisible.value)
    return;
  setTimeout(() => {
    if (!mouseIn)
      return;
    imageVisible.value = true;
    return;
  }, 1000);
}

function onMouseLeave() {
  mouseIn = false;
}
</script>

<template>
  <q-item class="tw-w-full tw-h-full tw-p-0 tw-flex tw-items-center" dense>
    <div class="tw-w-full tw-h-full tw-flex tw-items-center tw-justify-between tw-gap-2">
      <ImagePreviewer v-if="agFieldUtil.isAttachmentExist()" v-model="imageVisible"
                      :src="agFieldUtil.computedUrl().value" :fileName="agFieldUtil.getFileName()"/>
      <div class="tw-hidden">
        <q-uploader
          ref="qUploaderRef"
          @start="loadingStore.startLoading()"
          @finish="loadingStore.stopLoading()"
          :readonly="isColumnDisabled()"
          :url="FileUploaderWidgetUtil.getUrl"
          auto-upload flat dense hide-bottom-space no-thumbnails
          :multiple="false"
          @rejected="agFieldUtil.onRejected"
          @uploaded="agFieldUtil.onPartialUserInput"
        />
      </div>
      <div class="tw-flex tw-items-center tw-gap-1">
        <q-avatar
          v-if="agFieldUtil.eSignature"
          @click="FileUploaderWidgetUtil.onSignatureClick(agFieldUtil,!isColumnDisabled())"
          :class="{'nama-elevated-hover tw-cursor-pointer':!isColumnDisabled()}"
          size="md" class="tw-rounded-lg" square>
          <NamaIcon default-icon="nm-signature" class="tw-size-4" :class="[isColumnDisabled()?'icon-disabled':'active-primary']"/>
        </q-avatar>
        <q-avatar
          v-else-if="agFieldUtil.scanByDefault"
          :class="{'nama-elevated-hover tw-cursor-pointer':!isColumnDisabled()}"
          size="md"
          @click="onScanClick"
          class="tw-rounded-lg" square>
          <NamaIcon default-icon="nm-scan" class="tw-size-4" :class="[isColumnDisabled()?'icon-disabled':'active-primary']"/>
        </q-avatar>
        <q-avatar
          v-else-if="!agFieldUtil.eSignature && !agFieldUtil.scanByDefault"
          @click="e=>onAgAttachmentClick(e,true)"
          :class="{'nama-elevated-hover tw-cursor-pointer':!isColumnDisabled()}"
          size="md" class="tw-rounded-lg" square>
          <NamaIcon default-icon="nm-attachment" class="tw-size-4" :class="[isColumnDisabled()?'icon-disabled':'active-primary']"/>
        </q-avatar>
      </div>
      <a
        class="tw-overflow-hidden tw-inline-flex"
        v-if="agFieldUtil.isAttachmentExist()"
        :href="agFieldUtil.computedUrl().value">
        <q-chip
          class="nama-chip !tw-max-w-full !tw-w-full tw-m-0"
          :removable="!isColumnDisabled()"
          @remove="agFieldUtil.removeFile(!isColumnDisabled())"
          clickable
          @mouseenter="onMouseEnter"
          @mouseleave="onMouseLeave"
          square
          :label="agFieldUtil.getFileName()"
          :title="agFieldUtil.getFileName()">
        </q-chip>
      </a>
    </div>
  </q-item>
</template>
